<script setup lang="ts">
import ConfirmDeleteModal from "~/components/Common/Modals/ConfirmDeleteModal.vue";
import NotifyAttendeeModal from "~/components/Common/Modals/NotifyAttendeeModal.vue";
import UserRemovalActionModal from "~/components/Pages/training-plan/modals/UserRemovalActionModal.vue";
import type { RemoveBookingDtoRequestActionEnum } from "@simbelapp/order-sdk";

enum FlowStep {
  DELETE = "delete",
  REQUEST = "request",
  NOTIFY = "notify",
}

interface Props {
  // Flow configuration
  hasLinkedRequest?: boolean;
  linkedAttendeesCount?: number;
  showNotifyStep?: boolean;

  // Delete modal configuration
  deleteTitle?: string;
  deleteDescription?: string;
  deleteConsequences?: string[];
  deleteCancelText?: string;
  deleteConfirmText?: string;

  // Notification modal configuration
  notifyTitle?: string;
  notifyDescription?: string;
  notifyConsequences?: string[];

  // Loading state
  loadingCancelNotify?: boolean;
  loadingConfirmNotify?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  hasLinkedRequest: false,
  linkedAttendeesCount: 0,
  showNotifyStep: false,
  deleteTitle: "Voulez-vous supprimer le participant ?",
  deleteDescription: "Cette action ne sera pas modifiable.",
  deleteConsequences: () => [
    'Si l\'inscription était liée à une demande, celle-ci ré-apparaîtra dans la section "À traiter".',
    "Les éventuels documents associés seront supprimés",
  ],
  deleteCancelText: "Annuler",
  deleteConfirmText: "Supprimer",
  notifyTitle: "Voulez-vous envoyer une notification au participant ?",
  notifyDescription: "Cette action ne sera pas modifiable.",
  notifyConsequences: () => [
    "Le prestataire sera notifié si vous avez renseigné un contact prestataire sur la session.",
  ],
  loadingCancelNotify: false,
  loadingConfirmNotify: false,
});

const emit = defineEmits<{
  hide: [];
  confirmDelete: [];
  confirmRequest: [action: RemoveBookingDtoRequestActionEnum, comment?: string];
  cancelNotify: [];
  confirmNotify: [];
}>();

// Flow state
const currentStep = ref<FlowStep>(FlowStep.DELETE);

// Expose the current step for the parent
defineExpose({
  currentStep,
  resetFlow: () => {
    currentStep.value = FlowStep.DELETE;
  },
});

function hideModal() {
  currentStep.value = FlowStep.DELETE;
  emit("hide");
}

function onConfirmDelete() {
  if (props.hasLinkedRequest) {
    currentStep.value = FlowStep.REQUEST;
  } else if (props.showNotifyStep) {
    currentStep.value = FlowStep.NOTIFY;
  } else {
    emit("confirmDelete");
  }
}

function onBackToDelete() {
  currentStep.value = FlowStep.DELETE;
}

function onConfirmRequest(action: RemoveBookingDtoRequestActionEnum, comment?: string) {
  if (props.showNotifyStep) {
    emit("confirmRequest", action, comment);
    currentStep.value = FlowStep.NOTIFY;
  } else {
    emit("confirmRequest", action, comment);
  }
}

function onBackToRequest() {
  currentStep.value = FlowStep.REQUEST;
}

function onCancelNotify() {
  emit("cancelNotify");
}

function onConfirmNotify() {
  emit("confirmNotify");
}
</script>

<template>
  <!-- Step 1: Delete confirmation -->
  <ConfirmDeleteModal
    v-if="currentStep === FlowStep.DELETE"
    :title="deleteTitle"
    :description="deleteDescription"
    :consequences="deleteConsequences"
    :cancel-text="deleteCancelText"
    :confirm-text="deleteConfirmText"
    @cancel="hideModal"
    @confirm="onConfirmDelete"
  />

  <!-- Step 2: Action on linked request -->
  <UserRemovalActionModal
    v-if="currentStep === FlowStep.REQUEST"
    :linked-attendees-count="linkedAttendeesCount"
    @close="hideModal"
    @back="onBackToDelete"
    @confirm="onConfirmRequest"
  />

  <!-- Step 3: Notification to attendee -->
  <NotifyAttendeeModal
    v-if="currentStep === FlowStep.NOTIFY"
    :title="notifyTitle"
    :description="notifyDescription"
    :consequences="notifyConsequences"
    :loading-cancel="loadingCancelNotify"
    :loading-confirm="loadingConfirmNotify"
    @hide="hideModal"
    @back="hasLinkedRequest ? onBackToRequest() : onBackToDelete()"
    @cancel-notify="onCancelNotify"
    @confirm-notify="onConfirmNotify"
  />
</template>

<style scoped lang="scss"></style>
